import React, { useState, useRef } from 'react';
import { Send } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';

interface InvestorSubscriptionFormProps {
  campaignId?: string;
}

const InvestorSubscriptionForm: React.FC<InvestorSubscriptionFormProps> = ({ campaignId }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    investorType: 'individual',
    investmentAmount: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!captchaValue) {
      toast.error('Veuillez valider le captcha');
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Configuration manquante');
      }

      const data = {
        campaign_id: campaignId,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
        investor_type: formData.investorType,
        investment_amount: formData.investmentAmount ? parseFloat(formData.investmentAmount) : null,
        message: formData.message,
        ip_address: typeof window !== 'undefined' ? window.location.hostname : undefined,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      };

      const { error: insertError } = await supabase
        .from('investor_leads')
        .insert(data);

      if (insertError) {
        console.error('Erreur insertion:', insertError);
        throw insertError;
      }

      const functionUrl = `${supabaseUrl}/functions/v1/send-form-email`;

      const emailResponse = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify({
          formType: 'investor_subscription',
          formData: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            country: formData.country,
            investor_type: formData.investorType,
            investment_amount: formData.investmentAmount,
            message: formData.message,
          },
          customerEmail: formData.email,
          customerName: `${formData.firstName} ${formData.lastName}`,
        }),
      });

      if (!emailResponse.ok) {
        console.warn('Email non envoyé mais données sauvegardées');
      }

      toast.success('Votre demande a été envoyée avec succès! Nous vous contacterons très prochainement.');

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        country: '',
        investorType: 'individual',
        investmentAmount: '',
        message: '',
      });

      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setCaptchaValue(null);
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer ou nous contacter directement.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
            Prénom *
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Jean"
            required
          />
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
            Nom *
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Dupont"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="jean.dupont@example.com"
            required
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Téléphone *
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="+33 6 12 34 56 78"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
            Pays *
          </label>
          <input
            type="text"
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="France"
            required
          />
        </div>

        <div>
          <label htmlFor="investorType" className="block text-sm font-medium text-gray-700 mb-1">
            Type d'investisseur *
          </label>
          <select
            id="investorType"
            name="investorType"
            value={formData.investorType}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            required
          >
            <option value="individual">Particulier</option>
            <option value="institutional">Institutionnel</option>
            <option value="corporate">Corporate</option>
            <option value="family_office">Family Office</option>
            <option value="fund">Fonds d'Investissement</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="investmentAmount" className="block text-sm font-medium text-gray-700 mb-1">
          Montant envisagé (€)
        </label>
        <input
          type="number"
          id="investmentAmount"
          name="investmentAmount"
          value={formData.investmentAmount}
          onChange={handleChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="50000"
          min="0"
          step="1000"
        />
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
          Message (optionnel)
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 h-32"
          placeholder="Parlez-nous de votre intérêt pour ce projet..."
        />
      </div>

      <div className="flex justify-center">
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
          onChange={handleCaptchaChange}
        />
      </div>

      <button
        type="submit"
        className="w-full px-6 py-4 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={isSubmitting || !captchaValue}
      >
        {isSubmitting ? 'Envoi en cours...' : 'Souscrire maintenant'}
        <Send size={20} className="ml-2" />
      </button>

      <p className="text-xs text-gray-500 text-center mt-4">
        En soumettant ce formulaire, vous acceptez d'être contacté par Proxy Emobility et son cabinet partenaire concernant cette opportunité d'investissement.
      </p>
    </form>
  );
};

export default InvestorSubscriptionForm;
